import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { withTheme } from "emotion-theming";
import { useAuth } from "modules/auth";
import CompfestLogo from "components/CompfestLogo";

const navData = [
  {
    title: "Academy",
    color: "#2eaf76",
    content: [
      {
        title: "Startup Academy",
        desc: "Menyusun gagasan bisnismu dan merealisasikannya",
        link: "/academy/startup",
      },
      {
        title: "Data Science Academy",
        desc: "Perdalam kemampuan di dunia Machine Learning",
        link: "/academy/dsa",
      },
      {
        title: "Software Engineering Academy",
        desc: "Tingkatkan bakat dan kemampuan",
        link: "/academy/sea",
      },
    ],
  },
  {
    title: "Competition",
    color: "#d40f37",
    content: [
      {
        title: "Competitive Programming Contest",
        desc: "Tantang dan perkuat kemampuanmu",
        link: "/competition/cpc",
      },
      {
        title: "Indie Game Ignite",
        desc: "Kembangkan permainan dan asah kreativitas",
        link: "/competition/igi",
      },
      {
        title: "Business-IT Case",
        desc: "Bersaing dalam ide bisnis dan pemikiran analitis",
        link: "/competition/bizz-it",
      },
      {
        title: "Capture The Flag",
        desc: "Ungkap potensimu dalam menginvasi sistem",
        link: "/competition/ctf",
      },
      {
        title: "Innovative App Competition",
        desc: "Kembangkan aplikasi inovatif, beri dampak pada masyarakat",
        link: "/competition/iac",
      },
    ],
  },
  {
    title: "Seminar",
    link: "/seminar",
  },
  {
    title: "Blog",
    link: "/blog",
  },
  {
    title: "About",
    link: "/about",
  },
  {
    title: "Playground",
    link: "/play",
  },
];

const NavbarContainer = styled.nav`
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  height: 76px;
  width: 100%;
  :hover {
    background: ${props => props.theme.colors.backgroundSecondary};
  }

  background: ${props => props.theme.colors.background};
  transition: 0.2s;
  box-shadow: ${props =>
    props.top ? "none" : "0 0 3px 3px rgba(0, 0, 0, 0.1)"};

  @media screen and (max-width: 720px) {
    height: 50px;
  }
`;

const NavbarInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 32px;

  @media screen and (max-width: 600px) {
    padding: 0 16px;
  }
`;

const NavbarLogo = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  width: 20%;

  @media screen and (max-width: 720px) {
    width: 50%;
  }

  .logo-desktop {
    @media screen and (max-width: 600px) {
      display: none;
    }
  }

  .logo-mobile {
    display: none;

    @media screen and (max-width: 600px) {
      display: block;
    }
  }
`;

const NavbarMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  width: 80%;

  @media screen and (max-width: 720px) {
    width: 50%;
  }
`;

const Menu = styled.ul`
  list-style: none;
  display: flex;
  font-weight: bold;
  margin: 0;
  height: 100%;
  align-items: center;
  padding: 0;
  transition: 0.2s;

  @media screen and (max-width: 900px) {
    display: none;
  }

  &:hover > li > a,
  &:hover > li > span {
    color: ${props => props.theme.colors.body};

    &:not(:hover) {
      color: ${props => props.theme.colors.body}80;
    }
  }
`;

const MenuList = styled.li`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: 0.2s;

  & > a,
  & > span {
    color: inherit;
    padding: 0 20px;
    height: 100%;
    align-items: center;
    display: flex;
    color: ${props => props.theme.colors.body};

    &:hover + div,
    &:focus + div {
      display: flex;
    }
  }

  &:hover > div {
    display: flex;
  }

  &.user-btn {
    position: relative;

    &:hover > ul {
      display: block;
    }

    & > ul {
      position: absolute;
      top: 76px;
      right: 0;
      list-style: none;
      background: ${props => props.theme.colors.other[3]};
      padding: 0;
      display: none;
      box-shadow: 0 5px 5px 1px rgba(0, 0, 0, 0.1);

      li {
        &:hover {
          background: #0088d2;
        }

        a {
          display: flex;
          padding: 24px 32px;
          color: ${props => props.theme.colors.body};
          align-items: center;
          justify-content: flex-start;

          svg {
            margin-right: 16px;
          }
        }
      }
    }
  }
`;

const MenuChild = styled.div`
  position: fixed;
  top: 76px;
  left: 0;
  width: 100%;
  background: ${props => props.theme.colors.other[3]};
  display: none;
  transition: 0.2s;
  color: ${props => props.theme.colors.body};
  box-shadow: 0 5px 5px 1px rgba(0, 0, 0, 0.1);

  a {
    padding: 32px 64px;
    transition: 0.2s;
    color: inherit;

    &.disabled {
      pointer-events: none;
    }

    @media screen and (max-width: 1280px) {
      padding: 16px 32px;
    }

    @media screen and (max-width: 1024px) {
      padding: 8px 16px;
    }

    h4,
    h5 {
      font-family: ${props => props.theme.fonts.subheading};
      text-transform: none;
    }

    h5 {
      font-weight: normal;
      font-size: 1rem;
    }

    &:hover {
      background: ${props => props.color};
    }
  }
`;

const MenuMobileLogo = styled.div`
  display: none;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
  cursor: pointer;

  @media screen and (max-width: 900px) {
    display: flex;
  }

  .bar {
    width: 24px;
    height: 4px;
    transition: 0.2s;
  }

  .bar1,
  .bar2 {
    background: ${props => props.theme.colors.body};
  }

  .bar2 {
    width: 16px;
  }

  &.close {
    .bar1 {
      transform: translate(0, 4px) rotate(-45deg);
    }

    .bar2 {
      width: 24px;
      transform: translate(0, -4px) rotate(45deg);
    }
  }
`;

const MenuDrawer = styled.ul`
  position: fixed;
  top: 50px;
  right: ${props => (props.open ? "0" : "-100%")};
  height: calc(100% - 50px);
  width: 100%;
  z-index: 999;
  background: ${props => props.theme.colors.background};
  margin: 0;
  padding: 0;
  list-style: none;
  transition: 0.4s;
  overflow-y: auto;
  color: ${props => props.theme.colors.body};

  &.competition ul.competition,
  &.academy ul.academy {
    display: block;
  }
`;

const DrawerList = styled.li`
  font-weight: bold;
  cursor: pointer;
  position: relative;

  span,
  a {
    color: inherit;
    padding: 16px 32px;
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  svg {
    position: absolute;
    right: 32px;
    top: 20px;
  }
`;

const DrawerChild = styled.ul`
  padding: 0 16px;
  list-style: none;
  display: none;

  a {
    padding: 8px 32px;
    font-weight: normal;
    font-size: 14px;
  }
`;

const renderMenu = (data, user) => {
  return data
    .map(menu => (
      <MenuList key={menu.title}>
        {menu.link ? (
          <Link to={menu.link}>{menu.title}</Link>
        ) : (
          <a href="#">{menu.title}</a>
        )}
        {menu.content && renderMenuChild(menu.content, menu.color)}
      </MenuList>
    ))
    .concat([
      <MenuList key="login" className="user-btn">
        {user ? (
          <>
            <span>{user.shortName}</span>
            <ul>
              <li>
                <Link to="/dashboard">
                  <DashboardLogo />
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/dashboard/logout">
                  <LogoutLogo />
                  Logout
                </Link>
              </li>
            </ul>
          </>
        ) : (
          <Link to="/dashboard/login">Login</Link>
        )}
      </MenuList>,
    ]);
};

const renderMenuChild = (data, color) => {
  return (
    <MenuChild color={color}>
      {data.map(content => (
        <Link
          to={content.link}
          key={content.title}
          className={!content.link && "disabled"}
        >
          <h4>{content.title}</h4>
          <h5>{content.desc}</h5>
        </Link>
      ))}
    </MenuChild>
  );
};

const renderDrawer = (data, openChild, user) => {
  return data
    .map(menu =>
      menu.link ? (
        <DrawerList key={menu.title}>
          <Link to={menu.link}>{menu.title}</Link>
        </DrawerList>
      ) : (
        <DrawerList key={menu.title}>
          <span onClick={() => openChild(menu.title.toLowerCase())}>
            {menu.title}
          </span>
          <Arrow />
          <DrawerChild className={menu.title.toLowerCase()}>
            {renderDrawerChild(menu.content)}
          </DrawerChild>
        </DrawerList>
      )
    )
    .concat(
      user
        ? [
            <DrawerList key="dashboard">
              <Link to="/dashboard">Dashboard</Link>
            </DrawerList>,
            <DrawerList key="logout">
              <Link to="/dashboard/logout">Logout</Link>
            </DrawerList>,
          ]
        : [
            <DrawerList key="login">
              <Link to="/dashboard/login">Login</Link>
            </DrawerList>,
          ]
    );
};

const renderDrawerChild = data => {
  return data.map(content => (
    <li key={content.title}>
      <Link to={content.link}>{content.title}</Link>
    </li>
  ));
};

function Navbar() {
  const [mobileNavOpen, setMobileNav] = useState(false);
  const [drawerChild, setDrawerChild] = useState("");
  const [isTop, setTop] = useState(true);

  const changeMobileNav = () => {
    setMobileNav(!mobileNavOpen);
  };

  const openChild = section => {
    if (drawerChild === "") setDrawerChild(section);
    else if (drawerChild === section) setDrawerChild("");
    else setDrawerChild(section);
  };

  useEffect(() => {
    const handler = () => {
      if (window.scrollY === 0) {
        setTop(true);
      } else {
        setTop(false);
      }
    };

    window.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("scroll", handler);
    };
  }, []);

  const user = useAuth();

  return (
    <NavbarContainer top={isTop}>
      <NavbarInner>
        <NavbarLogo>
          <Link aria-label="Homepage" to="/">
            <CompfestLogo className="logo-desktop" />
            <CompfestLogoMobile className="logo-mobile" />
          </Link>
        </NavbarLogo>
        <NavbarMenu>
          <Menu>{renderMenu(navData, user)}</Menu>

          <MenuMobileLogo
            className={mobileNavOpen ? "close" : "open"}
            onClick={changeMobileNav}
          >
            <div className="bar bar1" />
            <div className="bar" />
            <div className="bar bar2" />
          </MenuMobileLogo>
          <MenuDrawer open={mobileNavOpen} className={drawerChild}>
            {renderDrawer(navData, openChild, user)}
          </MenuDrawer>
        </NavbarMenu>
      </NavbarInner>
    </NavbarContainer>
  );
}

const CompfestLogoMobile = withTheme(props => {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clip-path="url(#clip0-compfest-logo-mobile)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M3.60881 22.0693C3.38815 22.0726 3.16788 22.0493 2.9527 22C2.60729 21.9071 2.28532 21.7418 2.0079 21.5149C1.63508 21.2211 1.33429 20.8451 1.12871 20.4159C0.967475 20.0581 0.895408 19.6661 0.918754 19.274C0.92717 19.1422 0.949158 19.0117 0.984365 18.8845C0.992867 18.8513 0.99249 18.8164 0.983272 18.7834C0.974055 18.7503 0.956325 18.7203 0.931876 18.6964C0.602951 18.243 0.299569 17.7714 0.0231614 17.2839C0.00558262 17.26 -0.00390625 17.2311 -0.00390625 17.2014C-0.00390625 17.1716 0.00558262 17.1427 0.0231614 17.1188C0.173235 16.8754 0.339847 16.6428 0.521806 16.4225C0.68068 16.2259 0.849295 16.0376 1.02701 15.8581L1.08278 15.802C1.14839 15.736 1.19432 15.7129 1.26321 15.802C1.64048 16.34 2.01446 16.8317 2.41141 17.3268C2.73946 17.7228 3.06752 18.1056 3.41526 18.4819C3.763 18.8581 4.07137 19.1915 4.42895 19.5281C4.88403 19.9737 5.36142 20.3955 5.85928 20.7921C6.26278 21.1221 6.66957 21.4258 7.09605 21.7096C7.14853 21.7426 7.13213 21.7591 7.09605 21.7954C6.68749 22.2394 6.22958 22.6348 5.73133 22.9736C5.71789 22.9846 5.70109 22.9906 5.68377 22.9906C5.66644 22.9906 5.64965 22.9846 5.6362 22.9736C5.17498 22.7144 4.73016 22.4268 4.30429 22.1122C4.25743 22.0644 4.19818 22.0306 4.13324 22.0149C4.0683 21.9991 4.00027 22.002 3.93687 22.0231C3.86794 22.0395 3.79765 22.0494 3.72691 22.0528C3.68755 22.0693 3.6449 22.0495 3.60881 22.0693Z"
          fill={props.theme.colors.body}
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M22.8037 8.33339C22.4236 6.17132 21.34 4.19767 19.7232 2.72283C17.8758 0.960406 15.4226 -0.0151964 12.8767 5.40652e-05C12.3591 -0.00275321 11.8421 0.0392054 11.3316 0.125467C10.2383 0.298832 9.18091 0.652305 8.20192 1.17167C7.6586 1.45961 7.14441 1.80005 6.66662 2.18817C6.37927 2.41734 6.10439 2.66192 5.8432 2.92085C5.64637 3.12877 5.43641 3.32349 5.24942 3.54131C5.06243 3.75913 4.94105 3.89774 4.7967 4.08256C4.51362 4.45117 4.25498 4.83815 4.02249 5.24098C3.70983 5.77867 3.44736 6.34434 3.23844 6.93075C3.10778 7.2903 2.99824 7.65727 2.91038 8.02976C2.82181 8.4258 2.74964 8.82844 2.69387 9.23108C2.65778 9.4753 2.6545 9.72613 2.63482 9.97365C2.63482 10.0199 2.63482 10.0727 2.63482 10.1189V10.8911C2.65778 10.9439 2.63482 11.0034 2.65122 11.0595C2.65722 11.3647 2.68463 11.669 2.73323 11.9704C2.77342 12.2929 2.83368 12.6125 2.91366 12.9274C2.93007 12.9902 2.96287 13.0529 2.91366 13.1189C2.77916 13.2806 2.64138 13.4489 2.51016 13.6007C2.31233 13.8538 2.1294 14.1182 1.9623 14.3928C1.94817 14.41 1.94043 14.4316 1.94043 14.4538C1.94043 14.4761 1.94817 14.4977 1.9623 14.5149C2.10993 14.7591 2.25427 15.01 2.40846 15.2509C2.83622 15.9321 3.30742 16.5848 3.8191 17.2047C4.12419 17.571 4.43584 17.9208 4.75406 18.2707C4.90496 18.4357 5.08211 18.6007 5.2363 18.7492C5.39049 18.8977 5.607 19.1057 5.80056 19.2773C6.063 19.5149 6.33201 19.7393 6.60429 19.9604C6.95203 20.2443 7.31289 20.5182 7.68688 20.7756C8.06086 21.0331 8.42828 21.274 8.81539 21.4984C8.84694 21.5241 8.88632 21.5382 8.92693 21.5382C8.96754 21.5382 9.00692 21.5241 9.03847 21.4984C9.44504 21.235 9.82325 20.9297 10.167 20.5875C10.1965 20.5611 10.2227 20.5281 10.2621 20.5479C10.3015 20.5677 10.4065 20.5776 10.4754 20.5974C10.8327 20.6877 11.1954 20.7549 11.5612 20.7987C11.8302 20.8317 12.1058 20.835 12.3781 20.8548C12.4634 20.8548 12.5519 20.8548 12.634 20.8548C12.7103 20.8466 12.7873 20.8466 12.8636 20.8548C12.9489 20.8317 13.0342 20.8548 13.1195 20.8548C13.3491 20.8548 13.5755 20.8284 13.8051 20.8053C14.3595 20.738 14.9079 20.6276 15.4454 20.4753C16.3048 20.2315 17.1279 19.873 17.8927 19.4093C18.7793 18.8751 19.5792 18.2072 20.2645 17.4291C21.3836 16.2272 22.1996 14.7725 22.6437 13.1874C23.0878 11.6024 23.1472 9.93316 22.8168 8.32019L22.8037 8.33339ZM18.867 16.5842C17.7683 17.7299 16.3596 18.5262 14.8155 18.8746C14.0938 19.0542 13.3506 19.1309 12.6077 19.1024C10.5323 19.0425 8.55343 18.2071 7.05701 16.7591C5.72109 15.5186 4.82192 13.8751 4.49489 12.076C4.21968 10.7691 4.24976 9.41585 4.58278 8.1227C4.91581 6.82954 5.54267 5.63181 6.41402 4.62382C7.60374 3.22107 9.23284 2.26775 11.033 1.92085C11.6855 1.77705 12.3537 1.71826 13.0211 1.74593C14.8721 1.7985 16.6531 2.46967 18.083 3.65352C19.5682 4.85926 20.6126 6.52767 21.0519 8.39609C21.2217 9.0848 21.3055 9.79213 21.3012 10.5017C21.3208 12.7768 20.4486 14.968 18.8736 16.6007L18.867 16.5842Z"
          fill={props.theme.colors.body}
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.6962 3.44556C14.3089 3.44427 15.8736 3.99718 17.1312 5.01272C18.3889 6.02825 19.264 7.4455 19.6116 9.02972C20.0249 10.7778 19.7758 12.6182 18.9129 14.1914C18.8241 14.3816 18.7141 14.561 18.5848 14.7261C18.4009 14.9382 18.1646 15.0979 17.8999 15.1891C17.6351 15.2803 17.3512 15.2998 17.0765 15.2456C16.8019 15.1915 16.5463 15.0655 16.3354 14.8805C16.1245 14.6954 15.9658 14.4578 15.8751 14.1914C15.6607 13.5726 15.2162 13.0616 14.635 12.7657C14.2196 12.5414 13.7514 12.4349 13.2805 12.4576C12.8096 12.4802 12.3536 12.6312 11.9614 12.8944C11.439 13.2037 10.8533 13.3888 10.249 13.4356C9.64463 13.4824 9.03766 13.3898 8.47435 13.1647C7.91104 12.9396 7.40629 12.588 6.9986 12.1368C6.59092 11.6856 6.29108 11.1466 6.12197 10.5611C5.95866 10.0284 5.90354 9.46818 5.9599 8.91367C6.01626 8.35916 6.18294 7.82172 6.45003 7.33335C6.93562 6.36308 7.63684 5.51837 8.49932 4.86475C9.3618 4.21114 10.3623 3.76617 11.4234 3.56438C11.5743 3.53467 11.7252 3.51157 11.8794 3.49177C12.1503 3.45746 12.4232 3.44202 12.6962 3.44556ZM15.5864 17.0495C15.819 16.9982 16.0333 16.8841 16.2064 16.7195C16.2993 16.6199 16.3654 16.4981 16.3986 16.3657C16.4318 16.2332 16.4309 16.0945 16.3962 15.9625C16.3614 15.8305 16.2938 15.7095 16.1998 15.611C16.1058 15.5125 15.9884 15.4397 15.8587 15.3994C15.6716 15.3514 15.502 15.2506 15.3699 15.1089C15.2219 14.9464 15.1101 14.754 15.0418 14.5446C14.9307 14.2689 14.7303 14.039 14.4731 13.8922C14.2158 13.7454 13.9168 13.6904 13.6246 13.736C13.4249 13.7718 13.236 13.853 13.0721 13.9732C12.9082 14.0935 12.7737 14.2498 12.6789 14.4302C12.584 14.6105 12.5312 14.8103 12.5246 15.0143C12.5179 15.2182 12.5575 15.421 12.6404 15.6073C12.8254 16.0655 13.1436 16.4568 13.5533 16.7301C13.9631 17.0033 14.4453 17.1457 14.9368 17.1386C15.1567 17.1448 15.3761 17.1147 15.5864 17.0495Z"
          fill={props.theme.colors.body}
        />
      </g>
      <defs>
        <clipPath id="clip0-compfest-logo-mobile">
          <rect width="23" height="23" fill={props.theme.colors.body} />
        </clipPath>
      </defs>
    </svg>
  );
});

const Arrow = withTheme(props => {
  return (
    <svg
      width="11"
      height="9"
      viewBox="0 0 11 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.5 9L10.2631 0.749999L0.73686 0.749999L5.5 9Z"
        fill={props.theme.colors.body}
      />
    </svg>
  );
});

const DashboardLogo = withTheme(props => {
  return (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M12.4999 15.7891C6.79814 15.7891 4.42241 18.6231 3.94727 20.0401C4.65999 21.6932 7.36832 24.9996 12.4999 24.9996C17.6315 24.9996 20.3398 21.6932 21.0525 20.0401C20.5774 18.6231 18.2017 15.7891 12.4999 15.7891Z"
        fill={props.theme.colors.body}
      />
      <circle
        cx="12.5017"
        cy="10.0223"
        r="4.60526"
        fill={props.theme.colors.body}
      />
      <circle
        cx="12.5"
        cy="12.5"
        r="11.875"
        stroke={props.theme.colors.body}
        stroke-width="1.25"
      />
    </svg>
  );
});

const LogoutLogo = withTheme(props => {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 0H16V2V6H14.5V1.5H1.5V23.5H14.5V19H16V23.5V25H0V0Z"
        fill={props.theme.colors.body}
      />
      <path
        d="M18.375 8.5H6V12H24L18.375 5V8.5Z"
        fill={props.theme.colors.body}
      />
      <path
        d="M18.375 15.5H6V12H24L18.375 19V15.5Z"
        fill={props.theme.colors.body}
      />
    </svg>
  );
});

export default React.memo(Navbar);
